import EditableSectionCard from '@/components/ui/editable-section-card';
import { useCallback, useEffect, useState } from 'react';

// New data structure interface
interface CcHpiDataItem {
  reasonForVisit?: string;
  ccHpiHpiKey?: string;
  ccNameKey?: string;
  [key: string]: any; // Allow for other properties
}

interface CcHpiApiResponse {
  testKey: string;
  data: CcHpiDataItem[];
}

// Legacy data structure interface (for backward compatibility)
interface CcHpiLegacyData {
  ccNameKey?: string;
  ccHpiHpiKey?: string;
  [key: string]: any; // Allow for other properties in the JSON
}

// Union type to handle both new and legacy formats, including array format
type CcHpiData = CcHpiApiResponse | CcHpiLegacyData | CcHpiApiResponse[] | null;

interface CcHpiSectionProps {
  jsonContent: CcHpiData;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: CcHpiData) => Promise<void>;
  onAccept?: () => void;
  onHighlightSegments?: (segmentIds: string[]) => void; // New prop for highlighting segments
}

const CcHpiSection = ({
  jsonContent,
  isLoading,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
  onHighlightSegments,
}: CcHpiSectionProps) => {
  const [currentData, setCurrentData] = useState<CcHpiData>(null);

  // Helper function to extract the actual CC & HPI data from either format
  const extractCcHpiData = useCallback(
    (data: CcHpiData): CcHpiDataItem | null => {
      if (!data) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(data) && data.length > 0) {
        const firstItem = data[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          return firstItem.data[0] || null;
        }
      }

      // Check if it's the new API response format (single object)
      if ('testKey' in data && 'data' in data && Array.isArray(data.data)) {
        // New format: { testKey: "cchpi", data: [{ ... }] }
        return data.data[0] || null;
      }

      // Legacy format: direct object with ccNameKey and ccHpiHpiKey
      if ('ccNameKey' in data || 'ccHpiHpiKey' in data) {
        return data as CcHpiDataItem;
      }

      return null;
    },
    []
  );

  // Helper function to extract segment IDs for CC and HPI
  const extractSegmentIds = useCallback(
    (data: CcHpiData, fieldType: 'cc' | 'hpi'): string[] => {
      if (!data) return [];

      const segmentIds: string[] = [];

      // Helper function to recursively search for segment IDs
      const searchForSegmentIds = (obj: any, targetField: string) => {
        if (!obj || typeof obj !== 'object') return;

        // Check if this object has the target field
        if (Array.isArray(obj[targetField])) {
          console.log(`🔍 Found ${targetField} array:`, {
            segmentIds: obj[targetField],
            fieldType,
          });
          segmentIds.push(...obj[targetField].map((id: any) => String(id)));
        }

        // Also check for other possible field name variations
        const possibleFieldNames = [
          targetField,
          targetField.toLowerCase(),
          targetField.toUpperCase(),
          targetField.replace(/([A-Z])/g, '_$1').toLowerCase(), // camelCase to snake_case
        ];

        for (const fieldName of possibleFieldNames) {
          if (obj[fieldName] != null) {
            const value = obj[fieldName];
            if (Array.isArray(value)) {
              console.log(`🔍 Found ${fieldName} array:`, value);
              segmentIds.push(...value.map((id: any) => String(id)));
            } else if (value !== '') {
              console.log(`🔍 Found ${fieldName} single value:`, value);
              segmentIds.push(String(value));
            }
          }
        }

        // Recursively search through all properties
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (Array.isArray(value)) {
              value.forEach((item) => searchForSegmentIds(item, targetField));
            } else if (typeof value === 'object') {
              searchForSegmentIds(value, targetField);
            }
          }
        }
      };

      const targetField =
        fieldType === 'cc' ? 'ccSegmentsIds' : 'hpiSegmentsIds';
      searchForSegmentIds(data, targetField);

      // Remove duplicates and filter out empty/null values
      const uniqueSegmentIds = [
        ...new Set(segmentIds.filter((id) => id && id.trim() !== '')),
      ];

      console.log(`🔍 Extracted ${fieldType.toUpperCase()} segmentIds:`, {
        fieldType,
        originalSegmentIds: segmentIds,
        uniqueSegmentIds,
        count: uniqueSegmentIds.length,
      });

      return uniqueSegmentIds;
    },
    []
  );

  // Helper function to create the appropriate data structure for updates
  const createUpdateData = useCallback(
    (updatedFields: Record<string, string>): CcHpiData => {
      if (!jsonContent) return null;

      // Check if it's an array of API responses (newest format)
      if (Array.isArray(jsonContent) && jsonContent.length > 0) {
        const firstItem = jsonContent[0];
        if (
          'testKey' in firstItem &&
          'data' in firstItem &&
          Array.isArray(firstItem.data)
        ) {
          // Update the first item in the data array
          const updatedDataArray = [
            {
              ...firstItem.data[0],
              reasonForVisit:
                updatedFields.reasonForVisit || updatedFields.ccNameKey || '',
              ccHpiHpiKey: updatedFields.ccHpiHpiKey || '',
              ccNameKey:
                updatedFields.ccNameKey || updatedFields.reasonForVisit || '',
            },
          ];

          return [
            {
              ...firstItem,
              data: updatedDataArray,
            },
          ];
        }
      }

      // Check if it's the new API response format (single object)
      if (
        'testKey' in jsonContent &&
        'data' in jsonContent &&
        Array.isArray(jsonContent.data)
      ) {
        // Update the first item in the data array
        const updatedDataArray = [
          {
            ...jsonContent.data[0],
            reasonForVisit:
              updatedFields.reasonForVisit || updatedFields.ccNameKey || '',
            ccHpiHpiKey: updatedFields.ccHpiHpiKey || '',
            ccNameKey:
              updatedFields.ccNameKey || updatedFields.reasonForVisit || '',
          },
        ];

        return {
          ...jsonContent,
          data: updatedDataArray,
        };
      }

      // Legacy format: return direct object
      return {
        ...jsonContent,
        ccNameKey:
          updatedFields.ccNameKey || updatedFields.reasonForVisit || '',
        ccHpiHpiKey: updatedFields.ccHpiHpiKey || '',
      };
    },
    [jsonContent]
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    setCurrentData(jsonContent);
  }, [jsonContent]);

  // Handle updates to the CC & HPI data
  const handleUpdate = useCallback(
    async (updatedFields: Record<string, string>) => {
      if (!currentData) return;

      const updatedData = createUpdateData(updatedFields);

      // Update local state optimistically
      setCurrentData(updatedData);

      try {
        await onUpdate(updatedData);
      } catch (error) {
        // Revert local state on error
        setCurrentData(jsonContent);
        throw error;
      }
    },
    [currentData, jsonContent, onUpdate, createUpdateData]
  );

  // Handle clicking on CC field to highlight segments
  const handleCcClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!onHighlightSegments || !currentData) return;

      const ccSegmentIds = extractSegmentIds(currentData, 'cc');
      if (ccSegmentIds.length > 0) {
        console.log(
          `🎯 Highlighting ${ccSegmentIds.length} CC segments:`,
          ccSegmentIds
        );
        onHighlightSegments(ccSegmentIds);
      } else {
        console.log(`ℹ️ No CC segmentIds found`);
      }
    },
    [onHighlightSegments, currentData, extractSegmentIds]
  );

  // Handle clicking on HPI field to highlight segments
  const handleHpiClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!onHighlightSegments || !currentData) return;

      const hpiSegmentIds = extractSegmentIds(currentData, 'hpi');
      if (hpiSegmentIds.length > 0) {
        console.log(
          `🎯 Highlighting ${hpiSegmentIds.length} HPI segments:`,
          hpiSegmentIds
        );
        onHighlightSegments(hpiSegmentIds);
      } else {
        console.log(`ℹ️ No HPI segmentIds found`);
      }
    },
    [onHighlightSegments, currentData, extractSegmentIds]
  );

  // Extract the actual data for display
  const displayData = extractCcHpiData(currentData);

  // Prepare fields for the editable section card
  const fields = [
    {
      key: 'reasonForVisit',
      label: 'Chief Complaint (CC)',
      value: displayData?.reasonForVisit || displayData?.ccNameKey || '',
      placeholder: "Enter the primary reason for the patient's visit...",
      onClick: onHighlightSegments ? handleCcClick : undefined,
    },
    {
      key: 'ccHpiHpiKey',
      label: 'History of Present Illness (HPI)',
      value: displayData?.ccHpiHpiKey || '',
      placeholder: 'Describe the detailed history of the present illness...',
      onClick: onHighlightSegments ? handleHpiClick : undefined,
    },
  ];

  return (
    <EditableSectionCard
      title="CC & HPI"
      fields={fields}
      isLoading={isLoading}
      error={error}
      index={index}
      onUpdate={handleUpdate}
      onAccept={onAccept}
    />
  );
};

export default CcHpiSection;
